import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  fetchOtaDataProcessAdmin,
  fetchOtaSupplyBatchLogAdmin,
  OtaDataProcessRow,
  OtaSupplyLogSource,
  triggerBookingSupplyLogDownload,
} from './helper';
import {
  COLOR_BACKGROUND_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';

type OtaDataProcessProps = {
  adminPassword: string | null;
  displayTimeZone: string;
};

const TABLE_HEADER_BG = '#F1F5F9';
const TABLE_BG = '#FFFFFF';

type OtaDataProcessPeriod = 'month' | 'year' | 'q1' | 'q2' | 'q3' | 'q4';

/** API `source` values from list_ota_data_process_admin; `all` is UI-only filter. */
type OtaSourceFilter = 'all' | 'booking.com' | 'agoda' | 'hotels.com';

const OTA_SOURCE_ORDER: string[] = ['booking.com', 'agoda', 'hotels.com'];

function formatOtaSourceLabel(source: string): string {
  switch (source) {
    case 'booking.com':
      return 'Booking.com';
    case 'agoda':
      return 'Agoda';
    case 'hotels.com':
      return 'Hotels.com';
    default:
      return source;
  }
}

function formatIntHuman(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

function yearFromMonthInput(monthYyyyMm: string): string {
  const y = String(monthYyyyMm || '').slice(0, 4);
  return /^\d{4}$/.test(y) ? y : String(new Date().getUTCFullYear());
}

/** YYYY-MM for the calendar month in the admin dashboard timezone (month picker default). */
function currentCalendarMonthYyyyMm(timeZone: string): string {
  const tz = String(timeZone || '').trim() || Intl.DateTimeFormat().resolvedOptions().timeZone;
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
    }).formatToParts(new Date());
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    if (year && month) return `${year}-${month}`;
  } catch {
    /* invalid tz */
  }
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const OtaDataProcess = ({ adminPassword, displayTimeZone }: OtaDataProcessProps) => {
  const [rows, setRows] = useState<OtaDataProcessRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string>(() => currentCalendarMonthYyyyMm(displayTimeZone));
  const [selectedYear, setSelectedYear] = useState<string>(() =>
    currentCalendarMonthYyyyMm(displayTimeZone).slice(0, 4)
  );
  const [viewPeriod, setViewPeriod] = useState<OtaDataProcessPeriod>('month');
  const [sourceFilter, setSourceFilter] = useState<OtaSourceFilter>('all');
  const [logDownloadRowIds, setLogDownloadRowIds] = useState<Set<string>>(() => new Set());

  const load = useCallback(async () => {
    if (!adminPassword) {
      setRows([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const q =
      viewPeriod === 'month'
        ? { month: selectedMonth, period: 'month' }
        : { year: selectedYear, period: viewPeriod };
    const res = await fetchOtaDataProcessAdmin(adminPassword, q);
    setLoading(false);
    if (res.ok) {
      setRows(res.data.rows || []);
    } else {
      setRows([]);
      setError(res.error);
    }
  }, [adminPassword, selectedMonth, selectedYear, viewPeriod]);

  useEffect(() => {
    void load();
  }, [load]);

  const displayRows = useMemo(() => {
    if (sourceFilter === 'all') return rows;
    return rows.filter(r => r.source === sourceFilter);
  }, [rows, sourceFilter]);

  const totalsCaption = useMemo(() => {
    if (!rows.length) {
      return 'No rows returned for this period.';
    }
    const by = new Map<string, { rowSum: number; dates: Set<string> }>();
    for (const r of rows) {
      const s = String(r.source || '').trim() || 'unknown';
      let cur = by.get(s);
      if (!cur) {
        cur = { rowSum: 0, dates: new Set<string>() };
        by.set(s, cur);
      }
      cur.rowSum += Number(r.row_count) || 0;
      cur.dates.add(r.crawl_date);
    }
    const parts: string[] = [];
    for (const src of OTA_SOURCE_ORDER) {
      const cur = by.get(src);
      const label = formatOtaSourceLabel(src);
      const rowSum = cur?.rowSum ?? 0;
      const nDays = cur?.dates.size ?? 0;
      parts.push(`${label}: ${formatIntHuman(rowSum)} rows, ${nDays} day(s)`);
    }
    for (const [k, v] of by) {
      if (!OTA_SOURCE_ORDER.includes(k)) {
        parts.push(
          `${formatOtaSourceLabel(k)}: ${formatIntHuman(v.rowSum)} rows, ${v.dates.size} day(s)`
        );
      }
    }
    return parts.join(' · ');
  }, [rows]);

  const handleGetOtaLogs = useCallback(
    async (row: OtaDataProcessRow) => {
      if (!adminPassword) return;
      const src = String(row.source || '').trim() as OtaSupplyLogSource;
      if (src !== 'booking.com' && src !== 'agoda' && src !== 'hotels.com') return;
      setLogDownloadRowIds(prev => new Set(prev).add(row.id));
      setError(null);
      const res = await fetchOtaSupplyBatchLogAdmin(adminPassword, row.crawl_date, src);
      setLogDownloadRowIds(prev => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      const slug =
        src === 'booking.com' ? 'booking-com' : src === 'agoda' ? 'agoda' : 'hotels-com';
      triggerBookingSupplyLogDownload(res.data, `${slug}-supply-${row.crawl_date}.out`);
    },
    [adminPassword]
  );

  const columns = useMemo((): GridColDef<OtaDataProcessRow>[] => {
    return [
      {
        field: 'crawl_date',
        headerName: 'Date',
        width: 130,
      },
      {
        field: 'source',
        headerName: 'Source',
        flex: 1,
        minWidth: 140,
        valueFormatter: params => formatOtaSourceLabel(String(params.value ?? '')),
      },
      {
        field: 'row_count',
        headerName: 'Rows count',
        width: 130,
        type: 'number',
      },
      {
        field: 'supply_avg',
        headerName: 'Supply average',
        width: 160,
        type: 'number',
        valueFormatter: params => {
          const v = params.value as number | null | undefined;
          if (v == null || !Number.isFinite(Number(v))) return '—';
          return Number(v).toFixed(2);
        },
      },
      {
        field: 'get_logs',
        headerName: 'Get logs',
        width: 120,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: params => {
          const row = params.row;
          const s = String(row.source || '').trim();
          if (s !== 'booking.com' && s !== 'agoda' && s !== 'hotels.com') {
            return (
              <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                —
              </Typography>
            );
          }
          const busy = logDownloadRowIds.has(row.id);
          return (
            <Button
              size="small"
              variant="outlined"
              disabled={!adminPassword || busy}
              onClick={() => void handleGetOtaLogs(row)}
              sx={{ minWidth: 88, py: 0.5 }}
            >
              {busy ? <CircularProgress size={18} color="inherit" /> : 'Get logs'}
            </Button>
          );
        },
      },
    ];
  }, [adminPassword, logDownloadRowIds, handleGetOtaLogs]);

  const periodSummary = useMemo(() => {
    if (viewPeriod === 'month') return selectedMonth;
    if (viewPeriod === 'year') return `${selectedYear} (full year)`;
    return `${selectedYear} ${viewPeriod.toUpperCase()}`;
  }, [viewPeriod, selectedMonth, selectedYear]);

  const handlePeriodChange = (e: SelectChangeEvent<OtaDataProcessPeriod>) => {
    const next = e.target.value as OtaDataProcessPeriod;
    setViewPeriod(next);
    if (next !== 'month') {
      setSelectedYear(yearFromMonthInput(selectedMonth));
    }
  };

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          maxWidth: 1200,
          mx: 'auto',
          border: `1px solid ${COLOR_GREY_200}`,
          borderRadius: 2,
          bgcolor: COLOR_BACKGROUND_2,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        }}
      >
        <Typography variant="h5" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 0.25 }}>
          OTA data process
        </Typography>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 2 }}>
          Daily aggregates from <code>booking_com_supply</code>, <code>agoda_supply</code>, and{' '}
          <code>hotels_com_supply</code> in <code>Pre_Processed_OTA_Realestate</code>, grouped by{' '}
          <code>crawl_date</code> and source (row count and average <code>supply</code>).
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="ota-data-process-range-label">Range</InputLabel>
            <Select<OtaDataProcessPeriod>
              labelId="ota-data-process-range-label"
              label="Range"
              value={viewPeriod}
              onChange={handlePeriodChange}
            >
              <MenuItem value="month">Single month</MenuItem>
              <MenuItem value="year">Full year</MenuItem>
              <MenuItem value="q1">Q1 (Jan–Mar)</MenuItem>
              <MenuItem value="q2">Q2 (Apr–Jun)</MenuItem>
              <MenuItem value="q3">Q3 (Jul–Sep)</MenuItem>
              <MenuItem value="q4">Q4 (Oct–Dec)</MenuItem>
            </Select>
          </FormControl>
          {viewPeriod === 'month' ? (
            <TextField
              size="small"
              label="Month"
              type="month"
              value={selectedMonth}
              onChange={e => {
                const v = String(e.target.value || '');
                setSelectedMonth(v);
                setSelectedYear(yearFromMonthInput(v));
              }}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <TextField
              size="small"
              label="Year"
              type="number"
              inputProps={{ min: 2000, max: 2100 }}
              value={selectedYear}
              onChange={e => setSelectedYear(String(e.target.value || '').slice(0, 4))}
              sx={{ width: 120 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          <Typography variant="body2" sx={{ color: COLOR_TEXT_3 }}>
            Showing: <strong>{periodSummary}</strong> · oldest dates first
          </Typography>
        </Box>

        {adminPassword ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: COLOR_TEXT_3, display: 'block', mb: 0.75 }}>
              Filter by source
            </Typography>
            <ToggleButtonGroup
              size="small"
              color="primary"
              exclusive
              value={sourceFilter}
              onChange={(_e, v: OtaSourceFilter | null) => {
                if (v !== null) setSourceFilter(v);
              }}
              sx={{ flexWrap: 'wrap' }}
            >
              <ToggleButton value="all">All sources</ToggleButton>
              <ToggleButton value="booking.com">Booking.com</ToggleButton>
              <ToggleButton value="agoda">Agoda</ToggleButton>
              <ToggleButton value="hotels.com">Hotels.com</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mt: 1.25 }}>
              <strong>Totals for this period</strong>: {totalsCaption}
            </Typography>
          </Box>
        ) : null}

        {!adminPassword && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter admin password to load OTA supply aggregates.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: COLOR_PRIMARY_1 }} />
          </Box>
        ) : (
          <Box
            sx={{
              width: '100%',
              height: 560,
              border: `1px solid ${COLOR_GREY_200}`,
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: TABLE_BG,
            }}
          >
            <DataGrid
              rows={displayRows}
              columns={columns}
              pageSize={100}
              rowsPerPageOptions={[25, 50, 100]}
              disableSelectionOnClick
              density="compact"
              sx={{
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: TABLE_HEADER_BG,
                  fontWeight: 600,
                },
                '& .MuiDataGrid-main': { backgroundColor: TABLE_BG },
              }}
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OtaDataProcess;
